import { Plus } from "assets/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { UnitDTO, UnitType, UnitSubtype } from "shared/types/unit";
import { toast } from "react-toastify";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import {
  ArrowArcLeft,
  ArrowArcRight,
  Bathtub,
  Bed,
  Buildings,
  CalendarCheck,
  Check,
  Clock,
  CubeTransparent,
  Eye,
  HouseLine,
  Leaf,
  PencilSimple,
  PlusCircle,
  Sun,
  Stack,
  Toilet,
  Stairs,
  User,
  Warning,
  Wrench,
} from "@phosphor-icons/react";
import { EPC } from "shared/types/epc";
import { ContractPhase, ContactType, MilestoneDateType } from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { SecondaryLabel } from "shared/types/file";
import {
  startOfToday,
  format,
  parse,
  eachDayOfInterval,
  endOfMonth,
  addMonths,
  getDay,
  isToday,
} from "date-fns";
import { EditableInputComponent } from "components/index";
import { useCanEditUnitIds } from "hooks/usePermissions";
import { ChangeUnitIdModal } from "screens/buildings/units/unit/components/ChangeUnitIdModal";
import { ChangeLogViewer, UnitIdChangeLog } from "screens/buildings/units/unit/components/ChangeLogViewer";

interface UnitsTabProps {
  buildingId: string;
}

interface UnitCardProps {
  unit: UnitDTO;
  onEyeClick: (unit: UnitDTO) => void;
  onEditIdClick?: (unit: UnitDTO) => void;
  canEditUnitIds?: boolean;
}

function getTenantNames(contractContacts: any[]): string | null {
  const names = contractContacts
    .filter((c) => c.contactType === ContactType.TENANT && c.resource?.name)
    .map((c) => c.resource.name);
  return names.length > 0 ? names.join(", ") : null;
}

function getUnitOccupancy(unit: UnitDTO) {
  if (unit.vacantDueToRenovation) {
    return { status: "renovation" as const, tenantName: null, endDate: null };
  }

  const contracts: any[] = (unit as any).contracts || [];

  const activeContract = contracts.find((c) => c.phase === ContractPhase.ACTIVE);
  if (activeContract) {
    return {
      status: "active" as const,
      tenantName: getTenantNames(activeContract.contractContacts || []),
      endDate: null,
    };
  }

  const terminationContract = contracts.find(
    (c) => c.phase === ContractPhase.TERMINATION_PHASE,
  );
  if (terminationContract) {
    const endMilestone = (terminationContract.contractMilestoneDates || []).find(
      (m: any) => m.milestoneDateType === MilestoneDateType.END_DATE,
    );
    return {
      status: "termination" as const,
      tenantName: getTenantNames(terminationContract.contractContacts || []),
      endDate: endMilestone?.date ? new Date(endMilestone.date) : null,
    };
  }

  return { status: "vacant" as const, tenantName: null, endDate: null };
}

const OCCUPANCY_CONFIG = {
  active: {
    bg: "bg-status-good/10",
    border: "border-status-good/30",
    text: "text-status-good",
    label: "Actief contract",
    Icon: User,
  },
  termination: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-700",
    label: "Contract in opzeg",
    Icon: Warning,
  },
  vacant: {
    bg: "bg-brick-primary/10",
    border: "border-brick-primary/30",
    text: "text-brick-primary",
    label: "Leegstaand",
    Icon: HouseLine,
  },
  renovation: {
    bg: "bg-amber-50",
    border: "border-amber-300",
    text: "text-amber-700",
    label: "In renovatie",
    Icon: Wrench,
  },
};

const UnitCard = ({ unit, onEyeClick, onEditIdClick, canEditUnitIds }: UnitCardProps) => {
  const [isVisitMomentsModalOpen, setVisitMomentsModalOpen] = useState(false);
  const today = startOfToday();
  const [currMonth, setCurrMonth] = useState(format(today, "MMM-yyyy"));
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showHours, setShowHours] = useState([]);
  const getLatestEPC = (epcs: EPC[]) => {
    try {
      // Filter out EPCs that don't have any date information
      const validEPCs = epcs.filter(
        (epc) =>
          epc?.fileLog?.documentDate != null ||
          epc?.fileLog?.createdAt != null ||
          epc?.createdAt != null,
      );

      // Return null if no valid EPCs remain
      if (validEPCs.length === 0) {
        return null;
      }

      // Sort by date (most recent first)
      // Priority: documentDate > fileLog.createdAt > epc.createdAt
      const sortedEPCs = validEPCs.sort((a, b) => {
        const getDateValue = (epc: EPC): number => {
          if (epc?.fileLog?.documentDate) {
            return new Date(epc.fileLog.documentDate).getTime();
          }
          if (epc?.fileLog?.createdAt) {
            return new Date(epc.fileLog.createdAt).getTime();
          }
          if (epc?.createdAt) {
            return new Date(epc.createdAt).getTime();
          }
          return 0;
        };

        return getDateValue(b) - getDateValue(a);
      });

      return sortedEPCs[0].epcLabel;
    } catch (error) {
      // Log error for Sentry tracking
      console.error("Error getting latest EPC:", error, { epcs });
      return null;
    }
  };
  const handleVisitMomentsModalOpen = () => {
    setVisitMomentsModalOpen(true);
  };
  const handleVisitMomentsModalClose = () => {
    setVisitMomentsModalOpen(false);
  };
  const dummySlots = [
    {
      date: "2024-04-25",
      isAvailable: true,
      timeRange: { from: "12:00", to: "14:00" },
    },
    {
      date: "2024-04-26",
      isAvailable: true,
      timeRange: { from: "12:00", to: "14:00" },
    },
    {
      date: "2024-04-27",
      isAvailable: false,
      timeRange: {},
    },
  ];

  useEffect(() => {
    setAvailableSlots(dummySlots as any);
  }, [currMonth]);

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  let firstDayOfMonth = parse(currMonth, "MMM-yyyy", new Date());

  const daysInMonth = eachDayOfInterval({
    start: firstDayOfMonth,
    end: endOfMonth(firstDayOfMonth),
  });
  const getPrevMonth = () => {
    setCurrMonth(format(addMonths(firstDayOfMonth, -1), "MMM-yyyy"));
    setSelectedDate(null); // Reset selected date
    setShowHours(false as any); // Hide timeslots
  };
  const getNextMonth = () => {
    setCurrMonth(format(addMonths(firstDayOfMonth, 1), "MMM-yyyy"));
    setSelectedDate(null); // Reset selected date
    setShowHours(false as any); // Hide timeslots
  };
  const handleDateClick = (day: any) => {
    const formattedDay = format(day, "yyyy-MM-dd");
    // Toggle timeslots display off if the same date is clicked again
    if (selectedDate === formattedDay) {
      setSelectedDate(null);
      setShowHours(false as any); // Hide timeslots
    } else {
      const selectedSlot = availableSlots.find(
        (slot) => (slot as any).date === formattedDay,
      );
      if (selectedSlot && (selectedSlot as any).isAvailable) {
        setSelectedDate(formattedDay as any);
        setShowHours(true as any); // Show timeslots
      } else {
        setSelectedDate(null);
        setShowHours(false as any) as any; // Hide timeslots if day is not available
      }
    }
    console.log(`Selected Date: ${formattedDay}`);
  };
  const [tijdslots, setTijdslots] = useState([
    { id: 1, timeFrom: "0:00", timeTo: "1:00" },
  ]);

  // Function to handle adding new tijdslots
  const addTijdslot = () => {
    const newId = tijdslots.length + 1;
    const newTijdslot = {
      id: newId,
      timeFrom: "0:00",
      timeTo: "1:00",
    };
    setTijdslots([...tijdslots, newTijdslot]);
  };
  const occupancy = getUnitOccupancy(unit);
  const { bg, border, text, label, Icon } = OCCUPANCY_CONFIG[occupancy.status];

  return (
    <>
      <div className="border-neutral-primary/20 flex cursor-pointer flex-col gap-4 rounded-lg border bg-white p-3 text-sm shadow-sm duration-250 will-change-transform hover:scale-[1.0124]">
        <header
          onClick={() => onEyeClick(unit)}
          className="flex items-center justify-between"
        >
          <Chip
            className="gap-1"
            classNames={{
              base: "h-fit bg-white px-2 border border-brick-primary text-brick-primary",
              content: "px-0 font-medium",
            }}
          >
            {unit.id} {unit.referencePlus ? `(${unit.referencePlus})` : ""}
          </Chip>
          <Chip
            className="gap-1"
            startContent={<Stairs weight="duotone" size={16} />}
            classNames={{
              base: "h-fit bg-white px-2 border border-neutral-secondary-mid text-neutral-secondary-mid",
              content: "px-0 font-medium",
            }}
          >
            {unit.floor}
          </Chip>
        </header>
        {/* Occupancy status */}
        <div
          className={`flex items-start gap-2 rounded-md border px-2.5 py-2 ${bg} ${border}`}
          onClick={() => onEyeClick(unit)}
        >
          <Icon size={15} weight="duotone" className={`mt-0.5 shrink-0 ${text}`} />
          <div className="min-w-0">
            <p className={`text-xs font-semibold ${text}`}>{label}</p>
            {occupancy.tenantName && (
              <p className="text-neutral-primary truncate text-xs font-medium">
                {occupancy.tenantName}
              </p>
            )}
            {occupancy.status === "termination" && occupancy.endDate && (
              <p className="text-xs text-amber-600">
                leeg op{" "}
                {occupancy.endDate.toLocaleDateString("nl-BE", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
            {occupancy.status === "termination" && !occupancy.endDate && (
              <p className="text-xs text-amber-600">einddatum onbekend</p>
            )}
          </div>
        </div>
        {/* <figure className="overflow-hidden rounded-md">
          <img
            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=3200&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt=""
          />
        </figure> */}
        <div
          className="border-neutral-primary/10 rounded-md border p-2"
          onClick={() => onEyeClick(unit)}
        >
          <ImagesContainer
            pageSize={1}
            canUpload={false}
            canOpenImage={false}
            variant="light"
            display="inline"
            secondaryLabel={SecondaryLabel.COMMERCIEEL}
            unitId={(unit?.id as string) || ""}
          />
        </div>
        <div className="flex flex-col gap-4" onClick={() => onEyeClick(unit)}>
          <div className="flex flex-col gap-1.5">
            <div className="bg-moss-primary/10 text-moss-primary flex w-fit rounded-md px-2 py-1 text-base font-medium">
              {applyMask(unit.estimatedRentalValue, "valuta")}
            </div>
            <p className="text-base font-medium">
              {unit.building?.googlePlaceLongAddress}
            </p>
          </div>
          <div className="divide-neutral-primary/10 border-neutral-primary/10 flex flex-col divide-y rounded-md border *:flex *:items-center *:justify-between *:gap-0.5 *:p-1.5 [&>*>p]:font-medium [&>*>span]:flex [&>*>span]:items-center [&>*>span]:gap-0.5">
            <div>
              <span>
                <CubeTransparent size={16} weight="duotone" />
                Oppervlakte
              </span>
              <p>{applyMask(unit.netSurface, "surface")}</p>
            </div>
            <div>
              <span>
                <Leaf size={16} weight="duotone" /> EPC
              </span>
              <p>
                {(unit?.energyPerformanceInfo?.epcs &&
                  unit?.energyPerformanceInfo?.epcs.length > 0 &&
                  getLatestEPC(unit?.energyPerformanceInfo.epcs)) ||
                  "Onbekend"}
              </p>
            </div>
            <div>
              <span>
                <Sun size={16} weight="duotone" /> Buitenruimte
              </span>
              <p>{unit.outsideSpace}</p>
            </div>
            <div>
              <span>
                <Buildings size={16} weight="duotone" /> Type
              </span>
              <p>{unit.unitType}</p>
            </div>
          </div>
          <ul className="[&>*>*:first-child]:text-neutral-secondary-mid *:border-neutral-primary/20 flex items-center gap-2 *:flex *:flex-1 *:items-center *:justify-center *:gap-1.5 *:rounded-full *:border *:px-2.5 *:py-0.5 *:font-medium">
            <li>
              <Bed size={18} weight="duotone" />
              {unit.unitInspectionInfo?.numBedrooms || "Niet ingevuld"}
            </li>
            <li>
              <Bathtub size={18} weight="duotone" />
              {unit.unitInspectionInfo?.numBathrooms || "Niet ingevuld"}
            </li>
            <li>
              <Toilet size={18} weight="duotone" />
              {unit.unitInspectionInfo?.numToilets || "Niet ingevuld"}
            </li>
          </ul>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip content="Bezoekmomenten bekijken">
            <a
              onClick={handleVisitMomentsModalOpen}
              className="border-neutral-primary/20 text-neutral-primary flex h-8 w-8 items-center justify-center rounded-full border"
            >
              <CalendarCheck size={20} weight="duotone" />
            </a>
          </Tooltip>
          <Tooltip content="Details bekijken">
            <a
              onClick={() => onEyeClick(unit)}
              className="border-neutral-primary/20 text-neutral-primary flex h-8 w-8 items-center justify-center rounded-full border"
            >
              <Eye size={20} weight="duotone" />
            </a>
          </Tooltip>
          {canEditUnitIds && onEditIdClick && (
            <Tooltip content="Unit ID wijzigen">
              <a
                onClick={(e) => {
                  e.stopPropagation();
                  onEditIdClick(unit);
                }}
                className="border-neutral-primary/20 text-moss-primary hover:bg-moss-primary/10 flex h-8 w-8 items-center justify-center rounded-full border transition-colors"
              >
                <PencilSimple size={20} weight="duotone" />
              </a>
            </Tooltip>
          )}
        </div>
      </div>
      <Modal
        isOpen={isVisitMomentsModalOpen}
        onClose={handleVisitMomentsModalClose}
        size="5xl"
        shouldBlockScroll={true}
        isDismissable={true}
        scrollBehavior="inside"
        backdrop="blur"
        className="p-8"
      >
        <ModalContent className="flex flex-col gap-8">
          <ModalHeader className="flex items-center justify-between p-0 font-normal">
            <div className="flex items-center gap-1">
              <CalendarCheck size={22} weight="duotone" />
              <p>Bezoekmomenten voor {unit.id}</p>
            </div>
            <ButtonNew
              //onClick={handleOpenAddContractModal}
              icon={<Check size={18} weight="bold" />}
              label="Bewaren"
              variant="primaryMoss"
            />
          </ModalHeader>
          <div className="flex flex-col gap-6">
            <section className="flex items-center gap-4">
              <EditableInputComponent
                label="Hoeveel simultane bezoekers?"
                editMode={true}
                isLoading={false}
                value="Vul in..."
                onChange={() => {}}
                //disabled={true}
              />
              <EditableInputComponent
                label="Bezoekduur (in minuten)"
                editMode={true}
                isLoading={false}
                value="Selecteer..."
                onChange={() => {}}
                //disabled={true}
              />
            </section>
            <div className="flex gap-12">
              <div className="bg-neutral-primary/10 flex flex-col gap-4 rounded-lg p-3">
                <div className="flex items-center justify-between pb-4">
                  <p className="text-xl font-medium">
                    {format(firstDayOfMonth, "MMMM yyyy")}
                  </p>
                  <div className="*:text-moss-primary flex items-center justify-evenly gap-6 *:h-6 *:w-6 *:cursor-pointer sm:gap-12">
                    <ArrowArcLeft onClick={getPrevMonth} />
                    <ArrowArcRight onClick={getNextMonth} />
                  </div>
                </div>
                <div className="grid grid-cols-7 place-items-center gap-6 rounded-full bg-white px-2 py-2 sm:gap-12">
                  {days.map((day, idx) => (
                    <div
                      key={idx}
                      className="text-neutral-primary text-sm font-medium"
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 place-items-center gap-6 py-6 sm:gap-12">
                  {daysInMonth.map((day, idx) => {
                    const dayFormatted = format(day, "yyyy-MM-dd");
                    const daySlot = availableSlots.find(
                      (slot) => (slot as any).date === dayFormatted,
                    );
                    const isAvailable = daySlot
                      ? (daySlot as any).isAvailable
                      : false;
                    return (
                      <div
                        key={idx}
                        className={`col-start-${getDay(day) + 1} ${
                          isAvailable ? "" : ""
                        }`}
                      >
                        <p
                          className={`bg-neutral-primary/10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-sm font-medium ${isToday(day) ? "bg-red-400 text-white" : ""} ${
                            isAvailable
                              ? "bg-status-good! text-white"
                              : "bg-status-bad! text-white"
                          } `}
                          onClick={() => handleDateClick(day)}
                        >
                          {format(day, "d")}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
              {showHours && selectedDate && (
                <section className="border-neutral-primary/10 flex flex-col items-center gap-5 rounded-lg border p-3">
                  {tijdslots.map((slot, index) => (
                    <section key={slot.id} className="flex flex-col gap-3">
                      <header className="flex items-center gap-2 text-sm">
                        <Clock size={18} weight="bold" />
                        <p className="font-medium">Tijdslot {index + 1}</p>
                      </header>
                      <div className="flex gap-4">
                        <select
                          value={slot.timeFrom}
                          onChange={(e) => {
                            const newSlots = [...tijdslots];
                            newSlots[index].timeFrom = e.target.value;
                            setTijdslots(newSlots);
                          }}
                          className="cursor-pointer rounded-sm border px-2 py-1 text-sm"
                        >
                          {Array.from({ length: 24 }, (_, i) => `${i}:00`).map(
                            (time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ),
                          )}
                        </select>
                        <span>tot</span>
                        <select
                          value={slot.timeTo}
                          onChange={(e) => {
                            const newSlots = [...tijdslots];
                            newSlots[index].timeTo = e.target.value;
                            setTijdslots(newSlots);
                          }}
                          className="cursor-pointer rounded-sm border px-2 py-1 text-sm"
                        >
                          {Array.from({ length: 24 }, (_, i) => `${i}:00`).map(
                            (time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </section>
                  ))}
                  <a
                    href="#"
                    onClick={addTijdslot}
                    className="text-moss-primary flex items-center gap-1 text-sm"
                  >
                    <PlusCircle weight="duotone" size={18} />
                    Tijdslot toevoegen
                  </a>
                </section>
              )}
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export const UnitsTab = ({ buildingId }: UnitsTabProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const canEditUnitIds = useCanEditUnitIds();
  const [units, setUnits] = useState<UnitDTO[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newUnit, setNewUnit] = useState({
    boxNumber: "",
    floor: "" as string,
    unitType: "" as UnitType | "",
    unitSubtype: "" as UnitSubtype | "",
    netSurface: "",
    grossSurface: "",
  });

  // Change Unit ID state
  const [selectedUnitForIdChange, setSelectedUnitForIdChange] = useState<UnitDTO | null>(null);
  const [isChangeIdModalOpen, setIsChangeIdModalOpen] = useState(false);
  const [isChangingId, setIsChangingId] = useState(false);
  const [changeLog, setChangeLog] = useState<UnitIdChangeLog | null>(null);
  const [isChangeLogViewerOpen, setIsChangeLogViewerOpen] = useState(false);

  const fetchUnits = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/building/${buildingId}`,
        { method: "GET" },
        session,
      );
      const fetchedUnits = await response.json();
      const unitsArray = Array.isArray(fetchedUnits) ? fetchedUnits : [];
      const sortedUnits = unitsArray.sort((a: UnitDTO, b: UnitDTO) =>
        (a.id || "").localeCompare(b.id || "", undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );
      setUnits(sortedUnits);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, [buildingId]);

  const handleEyeClick = (unit: any) => {
    navigate(`/unit/detail/${unit.id}`);
  };

  // Handle Unit ID Change
  const handleEditIdClick = (unit: UnitDTO) => {
    setSelectedUnitForIdChange(unit);
    setIsChangeIdModalOpen(true);
  };

  const handleChangeUnitId = async (newUnitId: string) => {
    if (!selectedUnitForIdChange?.id) return;

    setIsChangingId(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/units/${selectedUnitForIdChange.id}/change-id`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newUnitId })
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unit ID wijzigen mislukt');
      }

      const result: UnitIdChangeLog = await response.json();
      setChangeLog(result);
      setIsChangeIdModalOpen(false);
      setIsChangeLogViewerOpen(true);
      fetchUnits(); // Refresh the units list
      toast.success(`Unit ID succesvol gewijzigd naar ${newUnitId}`);
    } catch (error) {
      console.error('Error changing unit ID:', error);
      toast.error(error instanceof Error ? error.message : 'Unit ID wijzigen mislukt');
    } finally {
      setIsChangingId(false);
    }
  };

  const handleNavigateToNewUnit = () => {
    if (changeLog?.newUnitId) {
      setIsChangeLogViewerOpen(false);
      navigate(`/unit/detail/${changeLog.newUnitId}`);
    }
  };

  const resetForm = () => {
    setNewUnit({
      boxNumber: "",
      floor: "",
      unitType: "",
      unitSubtype: "",
      netSurface: "",
      grossSurface: "",
    });
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (!isCreating) {
      setIsCreateModalOpen(false);
      resetForm();
    }
  };

  const isFormValid = () => {
    return (
      newUnit.boxNumber.trim() !== "" &&
      newUnit.floor !== "" &&
      newUnit.unitType !== "" &&
      newUnit.unitSubtype !== ""
    );
  };

  const handleCreateUnit = async () => {
    if (!isFormValid()) {
      toast.error("Vul alle verplichte velden in (busnummer, verdieping, type, subtype)");
      return;
    }

    setIsCreating(true);
    try {
      const unitData = {
        buildingId,
        boxNumber: newUnit.boxNumber.trim(),
        floor: parseInt(newUnit.floor),
        unitType: newUnit.unitType,
        unitSubtype: newUnit.unitSubtype,
        ...(newUnit.netSurface && {
          netSurface: parseFloat(newUnit.netSurface),
        }),
        ...(newUnit.grossSurface && {
          grossSurface: parseFloat(newUnit.grossSurface),
        }),
      };

      const response = await fetchWithAuth(
        `${API_URL}/units/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(unitData),
        },
        session,
      );

      if (response.ok) {
        const createdUnit = await response.json();
        toast.success(`Unit ${createdUnit.id} succesvol aangemaakt`);
        setIsCreateModalOpen(false);
        resetForm();
        fetchUnits();
      } else {
        const error = await response.json();
        throw new Error(error.message || "Kon unit niet aanmaken");
      }
    } catch (error: any) {
      console.error("Error creating unit:", error);
      toast.error(error.message || "Fout bij aanmaken van unit");
    } finally {
      setIsCreating(false);
    }
  };

  const unitTypeOptions = Object.values(UnitType).map((type) => ({
    label: type,
    value: type,
  }));

  const unitSubtypeOptions = Object.values(UnitSubtype).map((subtype) => ({
    label: subtype,
    value: subtype,
  }));

  const floorOptions = Array.from({ length: 21 }, (_, i) => ({
    label: i.toString(),
    value: i.toString(),
  }));

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-3">
        <header className="flex justify-between">
          <div className="flex items-center gap-1.5 text-lg font-medium">
            <Stack weight="duotone" size={20} />
            <p>Units</p>
          </div>
          <ButtonNew
            icon={<Plus />}
            label="Unit toevoegen"
            variant="primaryMoss"
            onClick={handleOpenCreateModal}
          />
        </header>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {units.map((unit) => (
            <UnitCard
              key={unit.id}
              unit={unit}
              onEyeClick={handleEyeClick}
              onEditIdClick={handleEditIdClick}
              canEditUnitIds={canEditUnitIds}
            />
          ))}
        </div>
      </div>

      {/* Quick Create Unit Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        size="md"
      >
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-2">
              <Stack weight="duotone" size={20} />
              <span>Unit toevoegen aan {buildingId}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="flex flex-col gap-4">
              <Input
                label="Busnummer"
                placeholder="Bijv. 01, 02, A1..."
                value={newUnit.boxNumber}
                onChange={(e) =>
                  setNewUnit({ ...newUnit, boxNumber: e.target.value })
                }
                isRequired
                isDisabled={isCreating}
              />
              <Select
                label="Verdieping"
                placeholder="Selecteer verdieping"
                selectedKeys={newUnit.floor ? [newUnit.floor] : []}
                onChange={(e) =>
                  setNewUnit({ ...newUnit, floor: e.target.value })
                }
                isRequired
                isDisabled={isCreating}
              >
                {floorOptions.map((option) => (
                  <SelectItem key={option.value}>{option.label}</SelectItem>
                ))}
              </Select>
              <Select
                label="Type"
                placeholder="Selecteer type"
                selectedKeys={newUnit.unitType ? [newUnit.unitType] : []}
                onChange={(e) =>
                  setNewUnit({
                    ...newUnit,
                    unitType: e.target.value as UnitType,
                  })
                }
                isRequired
                isDisabled={isCreating}
              >
                {unitTypeOptions.map((option) => (
                  <SelectItem key={option.value}>{option.label}</SelectItem>
                ))}
              </Select>
              <Select
                label="Subtype"
                placeholder="Selecteer subtype"
                selectedKeys={newUnit.unitSubtype ? [newUnit.unitSubtype] : []}
                onChange={(e) =>
                  setNewUnit({
                    ...newUnit,
                    unitSubtype: e.target.value as UnitSubtype,
                  })
                }
                isRequired
                isDisabled={isCreating}
              >
                {unitSubtypeOptions.map((option) => (
                  <SelectItem key={option.value}>{option.label}</SelectItem>
                ))}
              </Select>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Netto oppervlakte (m²)"
                  placeholder="Optioneel"
                  type="number"
                  value={newUnit.netSurface}
                  onChange={(e) =>
                    setNewUnit({ ...newUnit, netSurface: e.target.value })
                  }
                  isDisabled={isCreating}
                />
                <Input
                  label="Bruto oppervlakte (m²)"
                  placeholder="Optioneel"
                  type="number"
                  value={newUnit.grossSurface}
                  onChange={(e) =>
                    setNewUnit({ ...newUnit, grossSurface: e.target.value })
                  }
                  isDisabled={isCreating}
                />
              </div>
              <p className="text-neutral-secondary-mid text-sm">
                Overige informatie kan je nadien invullen via de unit detail
                pagina.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={handleCloseCreateModal}
              isDisabled={isCreating}
            >
              Annuleren
            </Button>
            <Button
              color="primary"
              onPress={handleCreateUnit}
              isDisabled={!isFormValid() || isCreating}
              className="bg-moss-primary"
            >
              {isCreating ? (
                <>
                  <Spinner size="sm" color="white" />
                  <span className="ml-2">Aanmaken...</span>
                </>
              ) : (
                "Unit aanmaken"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change Unit ID Modal */}
      <ChangeUnitIdModal
        isOpen={isChangeIdModalOpen}
        onClose={() => {
          setIsChangeIdModalOpen(false);
          setSelectedUnitForIdChange(null);
        }}
        onConfirm={handleChangeUnitId}
        currentUnitId={selectedUnitForIdChange?.id || ''}
        buildingId={buildingId}
        isLoading={isChangingId}
      />

      {/* Change Log Viewer Modal */}
      <ChangeLogViewer
        isOpen={isChangeLogViewerOpen}
        onClose={() => setIsChangeLogViewerOpen(false)}
        changeLog={changeLog}
        onNavigateToNewUnit={handleNavigateToNewUnit}
      />
    </div>
  );
};
